import { useRef } from 'react'

import type {
  PrimaryActionsDefinitionFn,
  SecondaryActionsDefinition
} from '@factorialco/f0-react/dist/experimental'
import { EmployeesCollectionListDocument } from 'generated/resources/graphql'
import i18n from 'modules/core/lib/i18n'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useEmployeeFilters } from 'modules/employees/lib/filters'

const SUPPLEMENTS_COLLECTION_PER_PAGE = 25

export interface SupplementsEmployeesDataCollectionSourceOptions {
  contractDate: string
  primaryActions?: PrimaryActionsDefinitionFn
  secondaryActions?: SecondaryActionsDefinition
}

/**
 * DataCollection source for the Supplements employee list.
 * Uses the same GraphQL query and filter shape as the main employee list
 * (EmployeesCollectionList) so we get the same filtering UX (workplace, team,
 * legal entity, access status, search, etc.) with server-side filtering.
 * Restricts to employees whose contract started on or before contractDate.
 *
 * Deps are intentionally only scalars so the source is not recreated every
 * render (employeeFilters.* are new object refs each time and would cause
 * a fetch loop when passed as deps).
 */
export function useSupplementsEmployeesDataCollectionSource({
  contractDate,
  primaryActions,
  secondaryActions
}: SupplementsEmployeesDataCollectionSourceOptions) {
  const { company } = useCurrentSession()
  const { filters: employeeFilters, hasJobCatalogTreeFeature } = useEmployeeFilters()
  const employeeFiltersRef = useRef(employeeFilters)
  employeeFiltersRef.current = employeeFilters

  return useGraphqlDataCollectionSource(
    {
      document: EmployeesCollectionListDocument,
      perPage: SUPPLEMENTS_COLLECTION_PER_PAGE,
      mapVariables: ({ filters, sortings, search }) => {
        const teamIds = filters.team?.map((id: number) => id)
        const locationIds = filters.workplace?.map((id: number) => id)
        const jobLevelIds = !hasJobCatalogTreeFeature
          ? filters.jobRole?.map((id: string | number) => Number(id))
          : undefined
        const jobCatalogTreeNodeIds = hasJobCatalogTreeFeature
          ? filters.jobRole?.map((id: string | number) => id.toString())
          : undefined
        const legalEntityIds = filters.legalEntity?.map((id: number) => id)
        const timeoffManagerIds = filters.timeoffManager?.map((id: number) => id)
        const accessStatus = filters.accessStatus?.map((status: string) => status)
        const accessStatusWithDefault = accessStatus?.length
          ? accessStatus
          : ['not_invited', 'accepted', 'active', 'invited']

        return {
          companyId: company.id,
          reportingTo: filters.reportingTo?.map((id: number) => id),
          referenceContractLevelIds: jobLevelIds?.length ? jobLevelIds : undefined,
          sort: sortings?.[0],
          search: search ?? undefined,
          teamIds: teamIds?.length ? teamIds : undefined,
          locationIds: locationIds?.length ? locationIds : undefined,
          referenceContractTreeLevelUuids: jobCatalogTreeNodeIds?.length
            ? jobCatalogTreeNodeIds
            : undefined,
          legalEntityIds: legalEntityIds?.length ? legalEntityIds : undefined,
          timeoffManagerIds: timeoffManagerIds?.length ? timeoffManagerIds : undefined,
          referenceContractStartsOnLteq: contractDate,
          accessStatus: accessStatusWithDefault
        }
      },
      mapPagination: (data) => ({
        total: data.employees.employeesConnection.totalCount ?? 0
      }),
      mapData: (data) => data.employees.employeesConnection.nodes,
      itemUrl: () => '',
      sortings: {
        firstName: { label: i18n.t('employee_form.first_name') },
        lastName: { label: i18n.t('employee_form.last_name') },
        fullName: { label: i18n.t('common.name') }
      },
      defaultSortings: { field: 'firstName', order: 'asc' as const },
      search: { enabled: true },
      filters: {
        workplace: employeeFiltersRef.current.workplace,
        team: employeeFiltersRef.current.team,
        legalEntity: employeeFiltersRef.current.legalEntity,
        jobRole: employeeFiltersRef.current.jobRole,
        accessStatus: employeeFiltersRef.current.accessStatus,
        reportingTo: employeeFiltersRef.current.reportingTo,
        timeoffManager: employeeFiltersRef.current.timeoffManager
      },
      primaryActions,
      secondaryActions
    },
    [company.id, contractDate, hasJobCatalogTreeFeature, primaryActions, secondaryActions]
  )
}
